declare var ko: any;

export class DragAndDropHandler {

    private static dragSrcEl = null;
    private static previousStates = new Map();

    /**
     * Apply HTML5 drag-and-drop reordering to a Knockout `observableArray`.
     *
     * @remarks
     * - Uses native drag events (`dragstart`, `dragover`, `drop`) and sets `draggable=true`.
     * - Intended for desktop browsers; mobile/touch support is limited without a polyfill.
     * - If `options.dragHandleSelector` is provided, dragging only starts from that handle.
     * - If `options.collapseOnDrag` is true, items with `isCollapsed()` are collapsed during drag
     *   and restored on drag end.
     *
     * @example
     * ```ts
     * DragAndDropHandler.handleDragAndDrop(
     *   listEl,
     *   viewModel.items,
     *   '.row',
     *   { dragHandleSelector: '.grip', collapseOnDrag: true }
     * );
     * ```
     *
     * @param element - Container element where the `foreach` binding is applied (usually `$element`).
     * @param currentObservableArray - The Knockout observable array to reorder in-place.
     * @param childrenSelector - Selector for draggable children within `element`.
     * @param options - Optional configuration: `dragHandleSelector`, `collapseOnDrag`.
     */
    public static handleDragAndDrop(element: HTMLElement, currentObservableArray: any, childrenSelector: string, options: any = {}): void {

        const elementNodeList = element.querySelectorAll(`${childrenSelector}`);

        if (!elementNodeList || elementNodeList.length === 0) {
            return;
        }

        function handleDragStart(this: HTMLElement, e: DragEvent) {
            const target = e.target as HTMLElement;
            let isHandle = false;

            if (options.dragHandleSelector) {
                const handle = target.querySelector(options.dragHandleSelector) || target.closest(options.dragHandleSelector);
                if (!handle) {
                    e.preventDefault();
                    return false;
                }
                isHandle = true;
            }


            if (!isHandle) {
                const isInteractive = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].indexOf(target.tagName) !== -1;
                if (isInteractive) {
                    e.preventDefault();
                    return false;
                }
            }

            DragAndDropHandler.dragSrcEl = this;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', '');

            this.classList.add("active");

            if (options.collapseOnDrag) {
                setTimeout(() => {
                    const items = ko.unwrap(currentObservableArray);
                    items.forEach(item => {
                        if (item && typeof item.isCollapsed === 'function') {
                            DragAndDropHandler.previousStates.set(item, item.isCollapsed());
                            item.isCollapsed(true);
                        }
                    });
                }, 0);
            }

            return true;
        }

        function handleDragOver(this: HTMLElement, e: DragEvent) {
            e.preventDefault();

            const observableToArray = currentObservableArray();

            const dataDragSrcEl = ko.dataFor(DragAndDropHandler.dragSrcEl);
            const dataDragDestEl = ko.dataFor(this);

            const positionDragSrcEl = observableToArray.indexOf(dataDragSrcEl);
            const positionDragDestEl = observableToArray.indexOf(dataDragDestEl);

            if (positionDragSrcEl === -1 || positionDragDestEl === -1) {
                return false;
            }

            if (positionDragSrcEl < positionDragDestEl) {
                this.classList.add('after');
                this.classList.remove('before');
            } else if (positionDragSrcEl > positionDragDestEl) {
                this.classList.add('before');
                this.classList.remove('after');
            }

            e.dataTransfer.dropEffect = "move";
            return false;
        }

        function handleDragLeave(this: HTMLElement) {
            this.classList.remove('before', 'after');
        }

        function handleDrop(this: HTMLElement, e: DragEvent) {
            e.preventDefault();
            e.stopPropagation();

            const observableToArray = currentObservableArray();

            const dataDragSrcEl = ko.dataFor(DragAndDropHandler.dragSrcEl);
            const dataDragDestEl = ko.dataFor(this);

            const positionDragSrcEl = observableToArray.indexOf(dataDragSrcEl);
            const positionDragDestEl = observableToArray.indexOf(dataDragDestEl);

            if (positionDragSrcEl === -1 || positionDragDestEl === -1) {
                return false;
            }

            if (DragAndDropHandler.dragSrcEl !== this) {
                observableToArray.splice(positionDragDestEl, 0, observableToArray.splice(positionDragSrcEl, 1)[0]);
                currentObservableArray(observableToArray);
            }

            this.classList.remove('before', 'after');

            return false;
        }

        function handleDragEnd(this: HTMLElement, e: DragEvent) {
            this.classList.remove('active');

            if (options.collapseOnDrag) {
                DragAndDropHandler.previousStates.forEach((state, item) => {
                    if (item && typeof item.isCollapsed === 'function') {
                        item.isCollapsed(state);
                    }
                });
                DragAndDropHandler.previousStates.clear();
            }
            DragAndDropHandler.dragSrcEl = null;
        }

        elementNodeList.forEach(elem => {
            const htmlElem = elem as HTMLElement;
            if (!htmlElem.classList.contains('drag-element')) {
                htmlElem.classList.add('drag-element');
                htmlElem.addEventListener('dragstart', handleDragStart);
                htmlElem.addEventListener('dragenter', (e) => { e.preventDefault(); });
                htmlElem.addEventListener('dragover', handleDragOver);
                htmlElem.addEventListener('dragleave', handleDragLeave);
                htmlElem.addEventListener('drop', handleDrop);
                htmlElem.addEventListener('dragend', handleDragEnd);
            }
            htmlElem.draggable = true;
        });
    }
}

/**
 * Knockout custom binding for drag-and-drop reordering.
 *
 * @remarks
 * Use this binding on the container that has the `foreach`. The binding will
 * wire up drag handlers after `childrenComplete`.
 * Mobile/touch support is limited without a drag-and-drop polyfill.
 *
 * @example
 * ```html
 * <ul
 *   data-bind="
 *     foreach: myObservableArray,
 *     dragAndDrop: {
 *       observableArray: myObservableArray,
 *       childrenSelector: '.my-class',
 *       dragHandleSelector: '.grip',
 *       collapseOnDrag: true
 *     }
 *   "
 * >
 *   <li class="my-class">
 *     <span class="grip">↕</span>
 *     <span data-bind="text: name"></span>
 *   </li>
 * </ul>
 * ```
 *
 * @param element - Container element with `foreach`.
 * @param valueAccessor - Returns options: `{ observableArray, childrenSelector, dragHandleSelector?, collapseOnDrag? }`.
 */
ko.bindingHandlers.dragAndDrop = {
    init(element, valueAccessor) {
        ko.bindingEvent.subscribe(element, 'childrenComplete', () => {
            const options = ko.unwrap(valueAccessor());
            const { observableArray, childrenSelector } = options;
            DragAndDropHandler.handleDragAndDrop(element, observableArray, childrenSelector, options);
        });
    }
};



// WEBPACK FOOTER //
// ./js/utils/DragAndDropHandler.ts